import React, { useState, useEffect, useMemo } from 'react';
import { gamesAPI } from '../api/games';
import {
  Calendar, Gamepad2, Loader2, ExternalLink, Clock, TrendingUp,
  ChevronDown, ChevronUp, X, Plus, Check, Globe, Monitor, Smartphone,
  Filter, Search
} from 'lucide-react';

// Platform icon/abbreviation helpers
const PLATFORM_ICONS = {
  'PC (Microsoft Windows)': 'PC', 'PlayStation 5': 'PS5', 'PlayStation 4': 'PS4',
  'Xbox Series X|S': 'XSX', 'Xbox One': 'XB1', 'Nintendo Switch': 'NSW',
  'Mac': 'Mac', 'Linux': 'LNX', 'iOS': 'iOS', 'Android': 'AND',
  'Google Stadia': 'STA', 'PlayStation VR2': 'PSVR2'
};

const getPlatformLabel = (platform) => {
  return platform.abbreviation || PLATFORM_ICONS[platform.name] || platform.name;
};

// Website icons/labels
const WEBSITE_LABELS = {
  official: { label: 'Official', icon: Globe },
  steam: { label: 'Steam', icon: Monitor },
  epicgames: { label: 'Epic', icon: Monitor },
  gog: { label: 'GOG', icon: Monitor },
  youtube: { label: 'YouTube', icon: Monitor },
  twitter: { label: 'X/Twitter', icon: Globe },
  reddit: { label: 'Reddit', icon: Globe },
  discord: { label: 'Discord', icon: Globe },
  twitch: { label: 'Twitch', icon: Monitor },
  itch: { label: 'itch.io', icon: Monitor },
  instagram: { label: 'Instagram', icon: Smartphone },
  facebook: { label: 'Facebook', icon: Globe }
};

const UpcomingReleases = () => {
  const [games, setGames] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedGame, setSelectedGame] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [gameDetail, setGameDetail] = useState(null);
  const [addingGame, setAddingGame] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [platformFilter, setPlatformFilter] = useState('all');
  const [timeFilter, setTimeFilter] = useState('all');
  const [expandedCard, setExpandedCard] = useState(null);
  const [collapsedGroups, setCollapsedGroups] = useState({ 'Recently Released': true });

  const toggleGroup = (key) => {
    setCollapsedGroups(prev => ({ ...prev, [key]: !prev[key] }));
  };

  useEffect(() => {
    fetchUpcoming();
  }, []);

  const fetchUpcoming = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await gamesAPI.getUpcoming({ limit: 500, daysAhead: 365, includeRecent: 7 });
      setGames(response.data.games || []);
    } catch (err) {
      console.error('Failed to fetch upcoming releases:', err);
      setError('Failed to load upcoming releases. Check your IGDB/Twitch credentials.');
    } finally {
      setLoading(false);
    }
  };

  const handleGameClick = async (game) => {
    setSelectedGame(game);
    setDetailLoading(true);
    try {
      const response = await gamesAPI.getIGDBDetail(game.igdbId);
      setGameDetail(response.data.game);
    } catch (err) {
      console.error('Failed to fetch game detail:', err);
      setGameDetail(game); // Fall back to list data
    } finally {
      setDetailLoading(false);
    }
  };

  const handleAddToDatabase = async (game) => {
    setAddingGame(game.igdbId);
    try {
      await gamesAPI.create({
        title: game.name,
        publisher: game.publishers?.[0] || null,
        releaseDate: game.releaseDate,
        genres: game.genres || [],
        platforms: game.platforms?.map(p => p.name) || [],
        coverImageUrl: game.coverUrl || null,
        igdbId: String(game.igdbId),
        verified: true
      });
      // Update the local state to reflect it's now in the DB
      setGames(prev => prev.map(g =>
        g.igdbId === game.igdbId ? { ...g, inDatabase: true } : g
      ));
      if (gameDetail?.igdbId === game.igdbId) {
        setGameDetail(prev => ({ ...prev, inDatabase: true }));
      }
    } catch (err) {
      const msg = err.response?.data?.error || 'Failed to add game';
      alert(msg);
    } finally {
      setAddingGame(null);
    }
  };

  const closeModal = () => {
    setSelectedGame(null);
    setGameDetail(null);
  };

  // Filtering
  const allPlatforms = useMemo(() => {
    const platformSet = new Set();
    games.forEach(g => g.platforms?.forEach(p => platformSet.add(p.name)));
    return [...platformSet].sort();
  }, [games]);

  const filteredGames = useMemo(() => {
    let filtered = games;

    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(g =>
        g.name.toLowerCase().includes(term) ||
        g.publishers?.some(p => p.toLowerCase().includes(term)) ||
        g.developers?.some(d => d.toLowerCase().includes(term))
      );
    }

    if (platformFilter !== 'all') {
      filtered = filtered.filter(g =>
        g.platforms?.some(p => p.name === platformFilter)
      );
    }

    if (timeFilter !== 'all') {
      const now = new Date();
      filtered = filtered.filter(g => {
        if (!g.releaseDate) return false;
        const release = new Date(g.releaseDate);
        const daysUntil = (release - now) / (1000 * 60 * 60 * 24);
        switch (timeFilter) {
          case 'released': return daysUntil < 0;
          case 'thisMonth': return daysUntil >= 0 && daysUntil <= 30;
          case 'next3': return daysUntil >= 0 && daysUntil <= 90;
          case 'next6': return daysUntil >= 0 && daysUntil <= 180;
          default: return true;
        }
      });
    }

    return filtered;
  }, [games, searchTerm, platformFilter, timeFilter]);

  // Group games by month
  const groupedGames = useMemo(() => {
    const groups = {};
    const now = new Date();

    filteredGames.forEach(game => {
      if (!game.releaseDate) return;
      const date = new Date(game.releaseDate);
      const isPast = date < now;
      const key = isPast
        ? 'Recently Released'
        : date.toLocaleDateString('en-US', { year: 'numeric', month: 'long' });

      if (!groups[key]) groups[key] = [];
      groups[key].push(game);
    });

    return groups;
  }, [filteredGames]);

  const formatReleaseDate = (dateString) => {
    if (!dateString) return 'TBA';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  const getDaysUntil = (dateString) => {
    if (!dateString) return null;
    const now = new Date();
    const release = new Date(dateString);
    const days = Math.ceil((release - now) / (1000 * 60 * 60 * 24));
    if (days < 0) return `Released ${Math.abs(days)}d ago`;
    if (days === 0) return 'Releasing today!';
    if (days === 1) return 'Tomorrow';
    if (days <= 7) return `${days} days`;
    if (days <= 30) return `${Math.ceil(days / 7)} weeks`;
    return `${Math.ceil(days / 30)} months`;
  };

  const getCountdownColor = (dateString) => {
    if (!dateString) return 'text-white';
    const days = Math.ceil((new Date(dateString) - new Date()) / (1000 * 60 * 60 * 24));
    if (days < 0) return 'text-green-400';
    if (days <= 7) return 'text-red-400';
    if (days <= 30) return 'text-amber-400';
    return 'text-blue-400';
  };

  if (loading) {
    return (
      <div className="p-6 min-h-screen flex items-center justify-center" style={{ background: 'transparent' }}>
        <div className="text-center">
          <Loader2 className="w-10 h-10 animate-spin mx-auto text-sc-pink" />
          <p className="text-white mt-3 text-sm">Loading upcoming releases from IGDB...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6 min-h-screen" style={{ background: 'transparent' }}>
        <div className="bg-red-500/10 border border-red-500/20 rounded-xl p-6 text-center max-w-lg mx-auto mt-20">
          <p className="text-red-400">{error}</p>
          <button onClick={fetchUpcoming} className="mt-3 btn-pink text-sm px-4 py-2">
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 min-h-screen" style={{ background: 'transparent' }}>
      {/* Header */}
      <div className="mb-5">
        <h1 className="text-2xl font-bold text-white mb-1">Upcoming Releases</h1>
        <p className="text-white text-sm">{games.length} upcoming releases from IGDB</p>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3 mb-5">
        {/* Search */}
        <div className="relative flex-1 min-w-[200px] max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white" />
          <input
            type="text"
            placeholder="Search games, publishers, developers..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full bg-white/5 border border-white rounded-lg pl-9 pr-3 py-2 text-sm text-white placeholder-white focus:outline-none focus:border-sc-pink/50"
          />
        </div>

        {/* Time Filter */}
        <select
          value={timeFilter}
          onChange={(e) => setTimeFilter(e.target.value)}
          className="bg-white/5 border border-white rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-sc-pink/50 appearance-none cursor-pointer"
        >
          <option value="all">All Dates</option>
          <option value="released">Recently Released</option>
          <option value="thisMonth">This Month</option>
          <option value="next3">Next 3 Months</option>
          <option value="next6">Next 6 Months</option>
        </select>

        {/* Platform Filter */}
        <select
          value={platformFilter}
          onChange={(e) => setPlatformFilter(e.target.value)}
          className="bg-white/5 border border-white rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-sc-pink/50 appearance-none cursor-pointer"
        >
          <option value="all">All Platforms</option>
          {allPlatforms.map(p => (
            <option key={p} value={p}>{p}</option>
          ))}
        </select>

        <span className="text-white text-xs ml-auto">{filteredGames.length} results</span>
      </div>

      {/* Games grouped by month */}
      {Object.keys(groupedGames).length === 0 ? (
        <div className="text-center py-16">
          <Gamepad2 className="w-12 h-12 text-white/20 mx-auto mb-3" />
          <p className="text-white">No games match your filters</p>
        </div>
      ) : (
        Object.entries(groupedGames).map(([month, monthGames]) => {
          const isCollapsed = !!collapsedGroups[month];
          return (
            <div key={month} className="mb-6">
              <button
                onClick={() => toggleGroup(month)}
                className="flex items-center gap-3 mb-3 w-full text-left hover:text-white transition-colors group"
              >
                {isCollapsed ? (
                  <ChevronDown className="w-4 h-4 text-white group-hover:text-white" />
                ) : (
                  <ChevronUp className="w-4 h-4 text-white group-hover:text-white" />
                )}
                <h2 className="text-lg font-semibold text-white group-hover:text-white">{month}</h2>
                <div className="flex-1 h-px bg-white/10"></div>
                <span className="text-xs text-white">{monthGames.length} games</span>
              </button>

              {!isCollapsed && (
                <div className="space-y-2">
                  {monthGames.map((game) => (
                    <GameRow
                      key={game.igdbId}
                      game={game}
                      expanded={expandedCard === game.igdbId}
                      onToggle={() => setExpandedCard(expandedCard === game.igdbId ? null : game.igdbId)}
                      onClick={() => handleGameClick(game)}
                      onAdd={() => handleAddToDatabase(game)}
                      adding={addingGame === game.igdbId}
                      formatDate={formatReleaseDate}
                      getDaysUntil={getDaysUntil}
                      getCountdownColor={getCountdownColor}
                    />
                  ))}
                </div>
              )}
            </div>
          );
        })
      )}

      {/* Detail Modal */}
      {selectedGame && (
        <GameDetailModal
          game={gameDetail || selectedGame}
          loading={detailLoading}
          onClose={closeModal}
          onAdd={handleAddToDatabase}
          adding={addingGame === selectedGame.igdbId}
          formatDate={formatReleaseDate}
          getDaysUntil={getDaysUntil}
          getCountdownColor={getCountdownColor}
        />
      )}
    </div>
  );
};

// Game row component — SteamDB-style list item
const GameRow = ({ game, expanded, onToggle, onClick, onAdd, adding, formatDate, getDaysUntil, getCountdownColor }) => {
  return (
    <div className="bg-white/[0.03] border border-white rounded-lg hover:bg-white/[0.06] hover:border-white transition-all group">
      {/* Main row */}
      <div className="flex items-center gap-3 p-3 cursor-pointer" onClick={onClick}>
        {/* Cover */}
        {game.coverUrl ? (
          <img src={game.coverUrl} alt={game.name} className="w-12 h-16 object-cover rounded shadow-lg flex-shrink-0" />
        ) : (
          <div className="w-12 h-16 bg-white/10 rounded flex items-center justify-center flex-shrink-0">
            <Gamepad2 className="w-5 h-5 text-white" />
          </div>
        )}

        {/* Info */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <h3 className="font-semibold text-white text-sm truncate">{game.name}</h3>
            {game.inDatabase && (
              <span className="text-[10px] bg-green-500/20 text-green-400 px-1.5 py-0.5 rounded font-medium flex-shrink-0">
                IN DB
              </span>
            )}
          </div>
          <div className="flex items-center gap-2 mt-0.5">
            {(game.publishers?.length > 0 || game.developers?.length > 0) && (
              <span className="text-xs text-white truncate">
                {game.developers?.length > 0 ? game.developers[0] : game.publishers[0]}
                {game.publishers?.length > 0 && game.developers?.length > 0 && game.publishers[0] !== game.developers[0]
                  ? ` / ${game.publishers[0]}`
                  : ''}
              </span>
            )}
          </div>
          {/* Genres */}
          <div className="flex flex-wrap gap-1 mt-1">
            {game.genres?.slice(0, 3).map((genre, idx) => (
              <span key={idx} className="text-[10px] bg-white/5 text-white px-1.5 py-0.5 rounded">
                {genre}
              </span>
            ))}
          </div>
        </div>

        {/* Platforms */}
        <div className="hidden md:flex flex-wrap gap-1 max-w-[180px] justify-end flex-shrink-0">
          {game.platforms?.slice(0, 5).map((p, idx) => (
            <span key={idx} className="text-[10px] bg-blue-500/10 text-blue-400/80 px-1.5 py-0.5 rounded font-mono">
              {getPlatformLabel(p)}
            </span>
          ))}
        </div>

        {/* Hype/Follows */}
        <div className="hidden lg:flex flex-col items-end gap-0.5 flex-shrink-0 w-20">
          {game.hypes > 0 && (
            <div className="flex items-center gap-1 text-[10px] text-amber-400/70" title="IGDB Hype count">
              <TrendingUp className="w-3 h-3" />
              <span>{game.hypes}</span>
            </div>
          )}
          {game.follows > 0 && (
            <div className="flex items-center gap-1 text-[10px] text-white" title="IGDB Follows">
              <span>{game.follows} follows</span>
            </div>
          )}
        </div>

        {/* Release date + countdown */}
        <div className="flex flex-col items-end flex-shrink-0 w-28">
          <span className="text-xs text-white font-medium">{formatDate(game.releaseDate)}</span>
          <span className={`text-[10px] font-medium ${getCountdownColor(game.releaseDate)}`}>
            {getDaysUntil(game.releaseDate)}
          </span>
        </div>

        {/* Add button */}
        <div className="flex-shrink-0" onClick={(e) => e.stopPropagation()}>
          {game.inDatabase ? (
            <div className="w-8 h-8 rounded-lg bg-green-500/10 flex items-center justify-center" title="Already in database">
              <Check className="w-4 h-4 text-green-400" />
            </div>
          ) : (
            <button
              onClick={(e) => { e.stopPropagation(); onAdd(); }}
              disabled={adding}
              className="w-8 h-8 rounded-lg bg-sc-pink/10 hover:bg-sc-pink/20 flex items-center justify-center transition-colors disabled:opacity-50"
              title="Add to game database"
            >
              {adding ? (
                <Loader2 className="w-4 h-4 text-sc-pink animate-spin" />
              ) : (
                <Plus className="w-4 h-4 text-sc-pink" />
              )}
            </button>
          )}
        </div>

        {/* Expand toggle */}
        <button
          onClick={(e) => { e.stopPropagation(); onToggle(); }}
          className="w-6 h-6 flex items-center justify-center text-white/20 hover:text-white flex-shrink-0"
        >
          {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
        </button>
      </div>

      {/* Expanded section */}
      {expanded && (
        <div className="px-3 pb-3 border-t border-white/5">
          <div className="flex gap-4 pt-3">
            {game.backgroundUrl && (
              <img src={game.backgroundUrl} alt="" className="w-48 h-28 object-cover rounded" />
            )}
            <div className="flex-1 min-w-0">
              {game.summary && (
                <p className="text-xs text-white leading-relaxed line-clamp-3">{game.summary}</p>
              )}
              <div className="flex flex-wrap gap-2 mt-2">
                {Object.entries(game.websites || {}).slice(0, 6).map(([key, url]) => {
                  const info = WEBSITE_LABELS[key];
                  if (!info) return null;
                  return (
                    <a
                      key={key}
                      href={url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-1 text-[10px] text-white hover:text-sc-pink bg-white/5 hover:bg-sc-pink/10 px-2 py-1 rounded transition-colors"
                    >
                      <ExternalLink className="w-3 h-3" />
                      {info.label}
                    </a>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Detail modal
const GameDetailModal = ({ game, loading, onClose, onAdd, adding, formatDate, getDaysUntil, getCountdownColor }) => {
  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div
        className="bg-gray-900 border border-white rounded-xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-hidden"
        onClick={e => e.stopPropagation()}
      >
        {loading ? (
          <div className="p-12 text-center">
            <Loader2 className="w-8 h-8 animate-spin mx-auto text-sc-pink" />
            <p className="text-white text-sm mt-3">Loading game details...</p>
          </div>
        ) : (
          <>
            {/* Hero section with background */}
            <div className="relative h-48 overflow-hidden">
              {game.backgroundUrl ? (
                <img src={game.backgroundUrl} alt="" className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full bg-gradient-to-br from-sc-pink/20 to-purple-900/20" />
              )}
              <div className="absolute inset-0 bg-gradient-to-t from-gray-900 via-gray-900/60 to-transparent" />

              {/* Close button */}
              <button onClick={onClose} className="absolute top-3 right-3 w-8 h-8 bg-black/50 rounded-full flex items-center justify-center text-white hover:text-white">
                <X className="w-5 h-5" />
              </button>

              {/* Game title overlay */}
              <div className="absolute bottom-0 left-0 right-0 p-5 flex items-end gap-4">
                {game.coverUrl && (
                  <img src={game.coverUrl} alt={game.name} className="w-20 h-28 object-cover rounded-lg shadow-xl border border-white" />
                )}
                <div className="flex-1 min-w-0">
                  <h2 className="text-2xl font-bold text-white drop-shadow-lg">{game.name}</h2>
                  <div className="flex items-center gap-3 mt-1">
                    <span className="text-sm text-white">{formatDate(game.releaseDate)}</span>
                    <span className={`text-sm font-medium ${getCountdownColor(game.releaseDate)}`}>
                      {getDaysUntil(game.releaseDate)}
                    </span>
                  </div>
                </div>
                {/* Add button */}
                <div className="flex-shrink-0">
                  {game.inDatabase ? (
                    <div className="flex items-center gap-1.5 bg-green-500/20 text-green-400 px-3 py-1.5 rounded-lg text-sm font-medium">
                      <Check className="w-4 h-4" />
                      In Database
                    </div>
                  ) : (
                    <button
                      onClick={() => onAdd(game)}
                      disabled={adding}
                      className="flex items-center gap-1.5 btn-pink text-sm px-3 py-1.5 disabled:opacity-50"
                    >
                      {adding ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
                      Add to Database
                    </button>
                  )}
                </div>
              </div>
            </div>

            {/* Content */}
            <div className="p-5 overflow-y-auto max-h-[calc(90vh-192px)]">
              {/* Developer / Publisher */}
              <div className="flex flex-wrap gap-x-6 gap-y-2 mb-4">
                {game.developers?.length > 0 && (
                  <div>
                    <span className="text-[10px] text-white uppercase tracking-wider">Developer</span>
                    <p className="text-sm text-white">{game.developers.join(', ')}</p>
                  </div>
                )}
                {game.publishers?.length > 0 && (
                  <div>
                    <span className="text-[10px] text-white uppercase tracking-wider">Publisher</span>
                    <p className="text-sm text-white">{game.publishers.join(', ')}</p>
                  </div>
                )}
                {game.gameModes?.length > 0 && (
                  <div>
                    <span className="text-[10px] text-white uppercase tracking-wider">Game Modes</span>
                    <p className="text-sm text-white">{game.gameModes.join(', ')}</p>
                  </div>
                )}
              </div>

              {/* Summary */}
              {game.summary && (
                <div className="mb-4">
                  <p className="text-sm text-white leading-relaxed">{game.summary}</p>
                </div>
              )}

              {/* Stats row */}
              <div className="flex flex-wrap gap-3 mb-4">
                {game.hypes > 0 && (
                  <div className="bg-amber-500/10 border border-amber-500/20 rounded-lg px-3 py-2 text-center">
                    <TrendingUp className="w-4 h-4 text-amber-400 mx-auto mb-0.5" />
                    <p className="text-lg font-bold text-amber-400">{game.hypes}</p>
                    <p className="text-[10px] text-amber-400/60">Hypes</p>
                  </div>
                )}
                {game.follows > 0 && (
                  <div className="bg-blue-500/10 border border-blue-500/20 rounded-lg px-3 py-2 text-center">
                    <Clock className="w-4 h-4 text-blue-400 mx-auto mb-0.5" />
                    <p className="text-lg font-bold text-blue-400">{game.follows}</p>
                    <p className="text-[10px] text-blue-400/60">Follows</p>
                  </div>
                )}
                {game.rating && (
                  <div className="bg-green-500/10 border border-green-500/20 rounded-lg px-3 py-2 text-center">
                    <p className="text-lg font-bold text-green-400">{game.rating}%</p>
                    <p className="text-[10px] text-green-400/60">{game.ratingCount} ratings</p>
                  </div>
                )}
              </div>

              {/* Platforms */}
              {game.platforms?.length > 0 && (
                <div className="mb-4">
                  <span className="text-[10px] text-white uppercase tracking-wider">Platforms</span>
                  <div className="flex flex-wrap gap-1.5 mt-1">
                    {game.platforms.map((p, idx) => (
                      <span key={idx} className="text-xs bg-blue-500/10 text-blue-400 px-2 py-1 rounded font-medium">
                        {p.name}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Genres & Themes */}
              <div className="flex flex-wrap gap-x-6 gap-y-3 mb-4">
                {game.genres?.length > 0 && (
                  <div>
                    <span className="text-[10px] text-white uppercase tracking-wider">Genres</span>
                    <div className="flex flex-wrap gap-1 mt-1">
                      {game.genres.map((g, idx) => (
                        <span key={idx} className="text-xs bg-sc-pink/10 text-sc-pink px-2 py-0.5 rounded">
                          {g}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
                {game.themes?.length > 0 && (
                  <div>
                    <span className="text-[10px] text-white uppercase tracking-wider">Themes</span>
                    <div className="flex flex-wrap gap-1 mt-1">
                      {game.themes.map((t, idx) => (
                        <span key={idx} className="text-xs bg-purple-500/10 text-purple-400 px-2 py-0.5 rounded">
                          {t}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Links */}
              {game.websites && Object.keys(game.websites).length > 0 && (
                <div className="mb-4">
                  <span className="text-[10px] text-white uppercase tracking-wider">Links</span>
                  <div className="flex flex-wrap gap-2 mt-1">
                    {Object.entries(game.websites).map(([key, url]) => {
                      const info = WEBSITE_LABELS[key];
                      if (!info) return null;
                      return (
                        <a
                          key={key}
                          href={url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-1.5 text-xs text-white hover:text-sc-pink bg-white/5 hover:bg-sc-pink/10 px-3 py-1.5 rounded-lg transition-colors"
                        >
                          <ExternalLink className="w-3.5 h-3.5" />
                          {info.label}
                        </a>
                      );
                    })}
                    {game.igdbUrl && (
                      <a
                        href={game.igdbUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-1.5 text-xs text-white hover:text-sc-pink bg-white/5 hover:bg-sc-pink/10 px-3 py-1.5 rounded-lg transition-colors"
                      >
                        <ExternalLink className="w-3.5 h-3.5" />
                        IGDB
                      </a>
                    )}
                  </div>
                </div>
              )}

              {/* Similar Games */}
              {game.similarGames?.length > 0 && (
                <div>
                  <span className="text-[10px] text-white uppercase tracking-wider">Similar Games</span>
                  <div className="flex gap-2 mt-1 overflow-x-auto pb-2">
                    {game.similarGames.slice(0, 8).map((sg) => (
                      <div key={sg.igdbId} className="flex-shrink-0 w-20">
                        {sg.coverUrl ? (
                          <img src={sg.coverUrl} alt={sg.name} className="w-20 h-28 object-cover rounded" />
                        ) : (
                          <div className="w-20 h-28 bg-white/10 rounded flex items-center justify-center">
                            <Gamepad2 className="w-5 h-5 text-white/20" />
                          </div>
                        )}
                        <p className="text-[10px] text-white mt-1 truncate">{sg.name}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default UpcomingReleases;